import {
  SelectTabData,
  SelectTabEvent,
  Tab,
  TabList,
} from "@fluentui/react-components";
import { useCallback, useMemo, useState } from "react";
import { usePortfolios } from "apps/project-iq/hooks/usePortfolios";
import { usePractices } from "apps/project-iq/hooks/usePractices";
import { useManagePortfoliosPracticesStyles } from "./ManagePortfoliosPractices.styles";
import { LabelEntityList } from "./LabelEntityList";
import { PortfoliosPracticesTab } from "./ManagePortfoliosPractices.types";

export const ManagePortfoliosPractices = (): JSX.Element => {
  const styles = useManagePortfoliosPracticesStyles();
  const [activeTab, setActiveTab] =
    useState<PortfoliosPracticesTab>("portfolios");

  const {
    portfolios,
    isLoading: isPortfoliosLoading,
    createMutation: createPortfolioMutation,
    updateMutation: updatePortfolioMutation,
    deleteMutation: deletePortfolioMutation,
  } = usePortfolios();

  const {
    practices,
    isLoading: isPracticesLoading,
    createMutation: createPracticeMutation,
    updateMutation: updatePracticeMutation,
    deleteMutation: deletePracticeMutation,
  } = usePractices();

  const handleCreatePortfolio = useCallback(
    async (label: string) => {
      await createPortfolioMutation.mutateAsync(label);
    },
    [createPortfolioMutation],
  );

  const handleUpdatePortfolio = useCallback(
    async (id: string, label: string) => {
      await updatePortfolioMutation.mutateAsync({ id, label });
    },
    [updatePortfolioMutation],
  );

  const handleDeletePortfolio = useCallback(
    async (id: string) => {
      await deletePortfolioMutation.mutateAsync(id);
    },
    [deletePortfolioMutation],
  );

  const handleCreatePractice = useCallback(
    async (label: string) => {
      await createPracticeMutation.mutateAsync(label);
    },
    [createPracticeMutation],
  );

  const handleUpdatePractice = useCallback(
    async (id: string, label: string) => {
      await updatePracticeMutation.mutateAsync({ id, label });
    },
    [updatePracticeMutation],
  );

  const handleDeletePractice = useCallback(
    async (id: string) => {
      await deletePracticeMutation.mutateAsync(id);
    },
    [deletePracticeMutation],
  );

  const isPortfoliosSaving = useMemo(
    () =>
      createPortfolioMutation.isLoading ||
      updatePortfolioMutation.isLoading ||
      deletePortfolioMutation.isLoading,
    [
      createPortfolioMutation.isLoading,
      updatePortfolioMutation.isLoading,
      deletePortfolioMutation.isLoading,
    ],
  );

  const isPracticesSaving = useMemo(
    () =>
      createPracticeMutation.isLoading ||
      updatePracticeMutation.isLoading ||
      deletePracticeMutation.isLoading,
    [
      createPracticeMutation.isLoading,
      updatePracticeMutation.isLoading,
      deletePracticeMutation.isLoading,
    ],
  );

  return (
    <div className={styles.root}>
      <div className={styles.tabBar}>
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_ev: SelectTabEvent, data: SelectTabData) =>
            setActiveTab(data.value as PortfoliosPracticesTab)
          }
          aria-label="Portfolios and Practices sections"
        >
          <Tab id="tab-portfolios" value="portfolios">
            Portfolios
          </Tab>
          <Tab id="tab-practices" value="practices">
            Practices
          </Tab>
        </TabList>
      </div>

      {activeTab === "portfolios" && (
        <div
          role="tabpanel"
          aria-labelledby="tab-portfolios"
          tabIndex={0}
          className={styles.tabContent}
        >
          <LabelEntityList
            items={portfolios}
            isLoading={isPortfoliosLoading || isPortfoliosSaving}
            entityName="Portfolio"
            emptyStateMessage="No portfolios yet."
            onCreate={handleCreatePortfolio}
            onUpdate={handleUpdatePortfolio}
            onDelete={handleDeletePortfolio}
          />
        </div>
      )}

      {activeTab === "practices" && (
        <div
          role="tabpanel"
          aria-labelledby="tab-practices"
          tabIndex={0}
          className={styles.tabContent}
        >
          <LabelEntityList
            items={practices}
            isLoading={isPracticesLoading || isPracticesSaving}
            entityName="Practice"
            emptyStateMessage="No practices yet."
            onCreate={handleCreatePractice}
            onUpdate={handleUpdatePractice}
            onDelete={handleDeletePractice}
          />
        </div>
      )}
    </div>
  );
};
